/**
 * Shared workflow activity-timeline entry.
 *
 * One row of a record's Activity card: the connector rail + coloured dot, the
 * event title (state/action label, with friendly labels for note / contact /
 * duplicate / assign / edit / evidence events), an emails-sent chip, the actor
 * (system / dealer / user) + timestamp, attachments, and any progression
 * override banner.
 *
 * Driven entirely by the generic ``ActionLogEntry`` from
 * ``useWorkflowHistory`` (the engine's action log), so ANY engine-backed
 * record - claims, transfers, inspections, ... - renders an identical timeline
 * by mapping its history through this one component inside a ``DetailCard``.
 * Extracted verbatim from the claims detail page so both surfaces share it
 * instead of each keeping a page-local copy.
 */

import {
  AlertTriangle,
  Building,
  CheckCircle2,
  Cpu,
  Layers,
  Mail,
  Paperclip,
  Pencil,
  StickyNote,
  User,
  UserPlus,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useFormatting } from '@/admin/hooks/use-formatting'
import type { ActionLogEntry } from '@/admin/hooks/use-claims'
import { SENDER_ROLE_LABELS } from '@/admin/pages/claims/claim-detail-helpers'
import { cn } from '@/shared/lib/utils'

interface HistoryAttachment {
  uuid: string
  file_url: string | null
  file_name: string
  file_type?: string
  file_size?: number
  mime_type: string
}

// WPS-157 §18: friendly labels for transfer events logged with an
// ``action_key`` only (no engine transition). Tuple is
// ``[translationKey, englishDefault]``; both keys are seeded in
// ``seed_translations.py``. Keys are transfer-specific, so this never
// affects claims/inspections entries.
const TRANSFER_EVENT_LABELS: Record<string, [string, string]> = {
  record_payment_received: [
    'transfers.activityPaymentReceived',
    'Payment received',
  ],
  verify_proof_of_ownership: [
    'transfers.activityProofVerified',
    'Proof of ownership verified',
  ],
  waive_fee: ['transfers.activityFeeWaived', 'Fee waived'],
  owner_updated_via_transfer: [
    'transfers.activityOwnerUpdated',
    'Ownership updated to new owner',
  ],
}

export function WorkflowTimelineEntry({
  entry,
  onClick,
  showConnector = false,
}: {
  entry: ActionLogEntry
  onClick?: () => void
  showConnector?: boolean
}) {
  const { formatDate, formatTime } = useFormatting()
  const { t } = useTranslation()
  const initiator =
    entry.initiated_by.type === 'SYSTEM'
      ? t('timeline.system', 'System')
      : entry.initiated_by.user?.name ||
        entry.initiated_by.user?.email ||
        t('timeline.unknown', 'Unknown')
  const isSystem = entry.initiated_by.type === 'SYSTEM'
  const isContact = entry.action_key === 'consumer_contact'
  const isNote = entry.action_key === 'note' || isContact
  const isDupResolve = entry.action_key === 'resolve_duplicates'
  const isDupReopen = entry.action_key === 'reopen_duplicates'
  const isDuplicateEvent = isDupResolve || isDupReopen
  const isAssign = entry.action_key === 'assign'
  const isEdit = entry.action_key === 'edit_details'
  const isEvidenceAdd = entry.action_key === 'evidence_added'
  const isEvidenceRemove = entry.action_key === 'evidence_removed'
  // WPS-157 §18: transfer actions that are not engine transitions (payment
  // received, proof verification, fee waiver, ownership handover to a new
  // owner) are logged as events so they appear on the Activity timeline.
  const transferEventLabel = TRANSFER_EVENT_LABELS[entry.action_key]
  const isTransferEvent = Boolean(transferEventLabel)
  const params = entry.parameters as Record<string, unknown> | null
  const senderRole = params?.sender_role as string | undefined
  const attachments = Array.isArray(params?.attachments)
    ? (params.attachments as HistoryAttachment[])
    : []
  const hasCapturedValues =
    entry.captured_values && Object.keys(entry.captured_values).length > 0
  const hasEmails = (entry.emails_sent ?? []).length > 0
  const isClickable =
    (isNote && (params?.message || attachments.length > 0)) ||
    hasCapturedValues ||
    hasEmails

  const dotColor = isContact
    ? 'border-[var(--blue)]'
    : isNote
      ? 'border-[var(--amber,#d97706)]'
      : isDupReopen
        ? 'border-[var(--status-warn)]'
        : isAssign
          ? 'border-[var(--blue)]'
          : 'border-[var(--green)]'

  // Generic engine logs duplicate-review events with action_key only (no
  // to_state), so give them a friendly label here.
  const duplicateLabel = isDupResolve
    ? 'Duplicates reviewed'
    : 'Duplicate review re-opened'

  // Assignment entries are logged with action_key="assign" and the current
  // (unchanged) state, so their to_state_label would otherwise read as a
  // duplicate of the state name. Surface the actual ownership change instead.
  const newAssignee = (entry.captured_values as Record<string, unknown> | null)
    ?.new_assignee as string | null | undefined
  const assignLabel = newAssignee
    ? t('claims.activityAssignedTo', 'Assigned to {{newAssignee}}', {
        newAssignee,
      })
    : t('claims.activityUnassigned', 'Unassigned')

  return (
    <div
      className={cn(
        'relative pb-[18px] pl-3',
        isClickable && 'cursor-pointer group',
      )}
      onClick={isClickable ? onClick : undefined}
    >
      {/* Connector line - rendered per entry so the rail stops at the last
          dot instead of hanging below it. Centred on the left-[-20px] dot;
          -bottom-1 extends it into the next entry's dot for a seamless rail. */}
      {showConnector && (
        <div className="absolute -bottom-1 left-[-15px] top-4 w-px bg-[var(--rule)]" />
      )}
      <div
        className={cn(
          'absolute left-[-20px] top-1 h-[11px] w-[11px] rounded-full border-[1.5px] bg-[var(--bg)]',
          dotColor,
        )}
      />
      <div>
        <span
          className={cn(
            'text-[13px] font-normal text-[var(--ink)]',
            isClickable && 'group-hover:underline',
          )}
        >
          {isContact && (
            <Mail className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--blue)]" />
          )}
          {isNote && !isContact && (
            <StickyNote className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--amber,#d97706)]" />
          )}
          {isDupResolve && (
            <CheckCircle2 className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--green)]" />
          )}
          {isDupReopen && (
            <Layers className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--status-warn)]" />
          )}
          {isAssign && (
            <UserPlus className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--blue)]" />
          )}
          {isEdit && (
            <Pencil className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--green)]" />
          )}
          {(isEvidenceAdd || isEvidenceRemove) && (
            <Paperclip
              className={cn(
                'mr-1.5 inline-block h-3.5 w-3.5',
                isEvidenceAdd ? 'text-[var(--green)]' : 'text-[var(--ink-3)]',
              )}
            />
          )}
          {isTransferEvent && !isEdit && (
            <CheckCircle2 className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--green)]" />
          )}
          {isDuplicateEvent
            ? duplicateLabel
            : isAssign
              ? assignLabel
              : isEdit
                ? t('claims.activityDetailsEdited', 'Details edited')
                : isEvidenceAdd
                  ? t('claims.activityEvidenceAdded', 'Evidence added')
                  : isEvidenceRemove
                    ? t('claims.activityEvidenceRemoved', 'Evidence removed')
                    : transferEventLabel
                      ? t(transferEventLabel[0], transferEventLabel[1])
                      : entry.reverts_closure
                        ? entry.action_label || entry.to_state_label
                        : entry.to_state_label || entry.action_label}
        </span>
        {hasEmails && (
          <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-[var(--blue)]">
            <Mail className="h-3 w-3" />
            {(entry.emails_sent ?? []).length} email
            {(entry.emails_sent ?? []).length === 1 ? '' : 's'} sent
          </span>
        )}
        <div className="mt-1 flex items-center gap-2">
          {isSystem ? (
            <Cpu className="h-3 w-3 text-[var(--ink-3)]" />
          ) : isNote && senderRole === 'dealer' ? (
            <Building className="h-3 w-3 text-[var(--ink-3)]" />
          ) : (
            <User className="h-3 w-3 text-[var(--ink-3)]" />
          )}
          <span className="text-[11px] text-[var(--ink-3)]">{initiator}</span>
          {senderRole && (
            <span className="rounded bg-[var(--bg-2,#f5f5f5)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-3)]">
              {SENDER_ROLE_LABELS[senderRole] || senderRole}
            </span>
          )}
          <span className="text-[10px] text-[var(--ink-3)]">
            {formatDate(entry.created_at)} {formatTime(entry.created_at)}
          </span>
        </div>
        {attachments.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[var(--ink-3)]">
            <Paperclip className="h-3 w-3" />
            <span>
              {attachments.length} attachment
              {attachments.length === 1 ? '' : 's'}
            </span>
            <span className="truncate">
              {attachments
                .map(
                  (attachment) =>
                    attachment.file_name || attachment.uuid.slice(0, 8),
                )
                .join(', ')}
            </span>
          </div>
        )}
        {entry.override && (
          <div className="mt-2 flex flex-col gap-1 rounded-md border-[0.5px] border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 shrink-0 text-[var(--status-warn)]" />
              <span className="text-[11px] font-medium text-[var(--ink-1)]">
                {t('claims.override.activityLabel', 'Progression overridden')}
                {entry.override.granted_by
                  ? ` - ${entry.override.granted_by}`
                  : ''}
              </span>
            </div>
            {entry.override.reason_labels.length > 0 && (
              <span className="text-[11px] text-[var(--ink-2)]">
                {entry.override.reason_labels.join(', ')}
              </span>
            )}
            {entry.override.comment && (
              <span className="text-[11px] italic text-[var(--ink-3)]">
                {entry.override.comment}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
